import api from '@/services/api'
import { Box, Button, Pagination, Typography } from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import React, { useEffect, useState } from 'react'
import { FiDownload } from 'react-icons/fi'
import RowsPerPageDropdown from '../RowsPerPageDropdown'
import Notfound from '../../../Notfound'
import { TableShimmer } from '../../../ShimmerUI'
import dayjs from 'dayjs'

const AgencyTransactionManagement: React.FC = () => {
  const perPage = 10
  const prevSize = sessionStorage.getItem('prevSize')
  const ourPageSize = prevSize ? parseInt(prevSize, 10) : 10
  const [pageSize, setPageSize] = useState(10)
  const [services, setServices] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [transactions, setTransactions] = useState<any[]>([])
  const [transactionId, setTransactionId] = useState('')
  const [subscriptionId, setSubscriptionId] = useState('')
  const [planName, setPlanName] = useState('')
  const [providerName, setProviderName] = useState('')
  const [planTypeFilter, setPlanTypeFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('All')
  const [planType, setPlanType] = useState('')
  const [loading, setLoading] = useState(false)
  const [transactionDate, setTransactionDate] = useState<Date | null>(null)
  const [searchKeyword, setSearchKeyword] = useState<string>('')
  const [selectedAccountStatus, setSelectedAccountStatus] = useState('')
  const [selectedSortBy, setSelectedSortBy] = useState('')
  const [searchTransactionid, setSearchTransactionid] = useState('')
  const [searchSubscriptionid, setSearchSubscriptionid] = useState('')
  const [loadingForShimmer, setLoadingForShimmer] = useState(true)
  const [totalAmount, setTotalAmount] = useState<number>(0)
  const [totalRecords, setTotalRecords] = useState<number>(0)
  const formatDateMMDDYY = (date: string | Date) => {
    const d = new Date(date)
    const month = d.getMonth() + 1 // months are zero-based, so add 1
    const day = d.getDate()
    const year = d.getFullYear().toString().slice(-4) // Get the last two digits of the year

    // Format as MM-DD-YY
    return `${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}-${year}`
  }

  const getFormattedDateForAPI = (date: Date | null): string | null => {
    if (!date) return null

    const year = date.getFullYear()
    const month = ('0' + (date.getMonth() + 1)).slice(-2)
    const day = ('0' + date.getDate()).slice(-2)

    return `${year}-${month}-${day}` // YYYY-MM-DD format
  }

  const handleAccountStatusChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedAccountStatus(event.target.value)
  }

  const handleSortChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSortBy(event.target.value)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
    fetchTransactions(page)
  }

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    sessionStorage.setItem('prevSize', newPageSize.toString())
  }
  const fetchTransactions = async (page?: number) => {
    setLoading(true)
    try {
      setLoadingForShimmer(true)
      const params: Record<string, any> = {
        page: page || 1, // Default to 1 if page is not provided
        limit: 10,
        status: selectedAccountStatus,
        sortOrder: selectedSortBy,
      }

      // Add filter parameters based on user input
      if (transactionId) {
        params.transactionId = transactionId.trim()
      }

      if (subscriptionId) {
        params.subscriptionId = subscriptionId.trim()
      }
      if (transactionDate) {
        params.transactionDate = getFormattedDateForAPI(transactionDate)
      }

      const response: any = await api.get('agency-membership/get-transactions', {
        params,
      })

      setTransactions(response.data.transaction || [])
      setTotalRecords(response?.data?.totalRecords || 0)
      setTotalAmount(response?.data?.totalAmount || 0)
      setTotalPages(Math.ceil(response?.data?.totalRecords / pageSize))
      /*  setSubscriptionTotal(response.data.totalRecords || 0)
      setSubscriptionActive(response.data.active || 0)
      setSubscriptionExpired(response.data.cancelled || 0)
     */
    } catch (error) {
      console.error('Error fetching subscriptions:', error)
      setTransactions([])
      setTotalRecords(0)
      setTotalAmount(0)
      // Handle error appropriately (e.g., show toast or alert)
    } finally {
      setLoading(false)
      setLoadingForShimmer(false)
    }
  }

  const handleReset = () => {
    setTransactionId('') // Reset Subscription ID
    setSubscriptionId('') // Reset Subscription ID
    setSelectedAccountStatus('')
    setSelectedSortBy('')
    setTransactionDate(null)
  }
  useEffect(() => {
    fetchTransactions()
  }, [
    transactionId,
    subscriptionId,
    selectedAccountStatus,
    selectedSortBy,
    transactionDate,
  ])

  return (
    <div className="p-6 mx-auto max-w-7xl">
      {/* Header & Filters */}
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-4 mb-2 sm:flex-row sm:items-center">
        <h1 className="text-2xl font-semibold text-[#6E7B8B]">Agency Transactions</h1>

        {/* Export CSV Button */}
      </div>

      {/* Search + Filters Row */}
      <div className="flex flex-wrap gap-4 mb-6">
        {/* <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Transaction Id
          </label>
          <input
            type="text"
            value={transactionId}
            onChange={e => setTransactionId(e.target.value)}
            placeholder="Transaction Id"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent max-w-[180px]"
          />
        </div> */}
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Subscription Id
          </label>
          <input
            type="text"
            value={subscriptionId}
            onChange={e => setSubscriptionId(e.target.value)}
            placeholder="Subscription Id"
            className="w-full border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent max-w-[180px]"
          />
        </div>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Payment Status
          </label>
          <select
            id="accountStatus"
            value={selectedAccountStatus}
            onChange={handleAccountStatusChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
          >
            <option value="" disabled selected>
              Select Status
            </option>
            <option value="Paid">Paid</option>
            <option value="Pending">Pending</option>
            <option value="Failed">Failed</option>
          </select>
        </div>

        {/* <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Sort by
          </label>
          <select
            id="sortBy"
            value={selectedSortBy}
            onChange={handleSortChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#026730] focus:border-transparent"
          >
            <option value="" disabled selected>
              Select Sorting
            </option>
            <option value="ace">Ascending</option>
            <option value="des">Descending</option>
          </select>
        </div> */}

        {/* Transaction Date */}
        <div>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box>
              <Typography variant="body2" mb={0.5}>
                Transaction Date
              </Typography>
              <DatePicker
                value={transactionDate}
                onChange={(newValue: Date | null) =>
                  setTransactionDate(newValue)
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: false,
                    sx: { width: '180px' },
                  },
                }}
              />
            </Box>
          </LocalizationProvider>
        </div>
        <div style={{ marginTop: 20 }}>
          <Box display="flex" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              onClick={handleReset} // Uncomment this when implementing reset logic
              sx={{
                borderRadius: 2,
                width: '113px',
              }}
            >
              Reset All
            </Button>
          </Box>
        </div>
      </div>

      {/* Results Summary */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {transactions.length} of {totalRecords} transactions
          {totalAmount > 0 && (
            <span style={{ marginLeft: '8px', fontWeight: 900 }}>
              • Total Amount: ${totalAmount.toFixed(2)}
            </span>
          )}
        </Typography>
      </Box>

      {/* Transactions Table */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-sm text-left text-white  uppercase  bg-[#5BC0BE]">
                <th className="px-6 py-3 font-medium">Subscription ID</th>
                <th className="px-6 py-3 font-medium">Transaction Date</th>
                <th className="px-6 py-3 font-medium">Agency Name</th>
                <th className="px-6 py-3 font-medium">Plan </th>
                <th className="px-6 py-3 font-medium">Invoice Id</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Amount</th>
                <th className="px-6 py-3 font-medium">Download Invoice</th>

                {/* <th className="px-6 py-3 font-medium">Transaction ID</th>
                {/* <th className="px-6 py-3 font-medium">Role</th> */}
                {/* <th className="px-6 py-3 font-medium">Payment Method</th> */}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loadingForShimmer ? (
                <TableShimmer columns={10} />
              ) : transactions?.length === 0 ? (
                <tr>
                  <td
                    colSpan={20}
                    className="p-4 text-sm text-center text-gray-500"
                  >
                    <Notfound />
                  </td>
                </tr>
              ) : (
                transactions.map(txn => (
                  <tr
                    key={txn.transactionId}
                    className="transition-colors hover:bg-gray-50"
                  >
                    {/* <td className="px-6 py-4">{txn.transaction_id}</td> */}
                    <td className="px-6 py-4">{txn.subscriptionId}</td>
                    <td className="px-6 py-4">
                      {txn.purchase_date
                        ? dayjs(txn.purchase_date).isValid()
                          ? dayjs(txn.purchase_date).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      {txn?.user_id === null ? (
                        '--'
                      ) : (
                        <>
                          {txn.user_id?.companyName || '--'}

                        </>
                      )}
                    </td>
                    <td className="px-6 py-4">{txn.plan_id?.name || '--'}</td>
                    <td className="px-6 py-4">{txn.invoiceId || '--'}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${txn.payment_status === 'Paid'
                          ? 'border-green-500 text-green-600'
                          : txn.payment_status === 'Pending'
                            ? 'border-yellow-500 text-yellow-600'
                            : 'border-red-500 text-red-600'
                          }`}
                      >
                        {txn.payment_status}
                      </span>
                    </td>
                    <td className="px-6 py-4">${txn.amount.toFixed(2)}</td>
                    <td className="px-6 py-4">
                      {txn.invoiceUrl ? (
                        <a
                          href={txn.invoiceUrl}
                          className="flex items-center gap-1 text-blue-600 hover:underline"
                        >
                          <FiDownload className="w-4 h-4" />
                          Download
                        </a>
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-3">
        <div className="flex items-center gap-4">
          <RowsPerPageDropdown
            pageSize={pageSize}
            onPageSizeChange={newPageSize => {
              handlePageSizeChange(newPageSize)
              if (ourPageSize && ourPageSize !== newPageSize) {
                setCurrentPage(1)
              }
            }}
          />
        </div>
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={handlePageChange}
          className="mt-1 mt-2 mt-md-0"
          style={{
            display: 'flex',
            justifyContent: 'center',
          }}
        />
      </div>
    </div>
  )
}

export default AgencyTransactionManagement
